// SolyticsMenus.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * SolyticsMenus.jsx
 *
 * Public API
 * ──────────
 * Props      Type           Req  Default  Description
 * ─────────  ─────────────  ───  ───────  ──────────────────────────────────────────
 * id         string          ✓    —       Root DOM id; binds aria-controls on trigger
 * items      MenuItem[]      ✓    —       See Item Shape below
 * anchorEl   HTMLElement     —    null    Trigger element (from event.currentTarget)
 * open       bool            ✓    —       Controlled open state
 * onClose    func            ✓    —       Called on outside-click or Escape
 * elevation  number          —    3       Paper depth; cascades to every submenu panel
 *
 * Item Shape
 * ──────────
 * {
 *   id:             string            REQUIRED — stable React key + DOM id
 *   content:        string|ReactNode  REQUIRED — string = text; JSX = rendered as-is
 *   onClick:        function          optional  — leaf handler; closes menu on call
 *   icon:           ComponentType|ReactElement optional — MUI icon class or element
 *   isItemDisabled: boolean           optional  — show but disable (default false)
 *   isItemVisible:  boolean           optional  — hide without removing (default true)
 *   divider:        boolean           optional  — Divider rendered below this item
 *   subMenus:       MenuItem[]        optional  — makes item a submenu trigger
 * }
 *
 * Submenus
 * ────────
 * • Opens on hover (80 ms delay to prevent flicker on cursor travel) AND on click
 *   (touch-safe). Both mechanisms work independently.
 * • Keyboard: ArrowRight / Enter opens; ArrowLeft closes and returns focus to trigger.
 * • Cascade: when root menu closes, every nested submenu closes immediately.
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  isValidElement,
} from 'react';
import PropTypes from 'prop-types';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import SolyticsPopper from './SolyticsPopper';
import Fade from '@mui/material/Fade';

import { ReactComponent as RightArrowIcon } from '../assets/RightArrowIcon.svg';

import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsDivider from './SolyticsDivider';

// ─────────────────────────────────────────────────────────────────────────────
// Internal utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a menu icon from either a pre-rendered React element or an MUI icon
 * component class. String URLs / <img> tags are intentionally unsupported.
 */
function renderIcon(icon) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  const Icon = icon;
  return <Icon fontSize="small" sx={{ color: 'text.primary' }} />;
}

/** Delay before closing a submenu — lets the cursor travel to the panel without flicker */
const CLOSE_DELAY_MS = 80;

// ─────────────────────────────────────────────────────────────────────────────
// LeafMenuItem (internal)
// Renders a single item that has no subMenus.
// Handles both plain-string content and arbitrary JSX content.
// ─────────────────────────────────────────────────────────────────────────────
const LeafMenuItem = React.forwardRef(
  ({ item, onMenuClose, ...props }, ref) => {
    const handleKeyDown = (e) => {
      // If user presses Enter/Space while focused on the row, simulate click on inner interactive elements
      if (
        (e.key === 'Enter' || e.key === ' ') &&
        e.target === e.currentTarget
      ) {
        const interactive = e.currentTarget.querySelector(
          'input, button, a, [role="switch"], [role="checkbox"]'
        );
        if (interactive) {
          e.preventDefault();
          e.stopPropagation();
          interactive.click();
          return;
        }
      }
      props.onKeyDown?.(e);
    };

    const handleClick = (e) => {
      props.onClick?.(e);
      item.onClick?.(e);

      // Don't close the menu if the user interacted with an input/switch
      const isInteractive = e.target.closest(
        'input, button, textarea, select, a, [role="switch"], [role="checkbox"]'
      );
      if (!isInteractive) {
        onMenuClose(e, 'itemClick');
      }
    };

    return (
      <MenuItem
        ref={ref}
        id={item.id}
        disabled={item.isItemDisabled}
        {...props}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <SolyticsBox
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          {renderIcon(item.icon)}
          {isValidElement(item.content) ? (
            item.content
          ) : (
            <SolyticsTypography
              variant="inherit"
              sx={{ color: 'text.primary' }}
            >
              {item.content}
            </SolyticsTypography>
          )}
        </SolyticsBox>
      </MenuItem>
    );
  }
);
LeafMenuItem.displayName = 'LeafMenuItem';

// ─────────────────────────────────────────────────────────────────────────────
// NestedMenuItem (internal)
// Renders a submenu trigger row + a Popper-based panel for its children.
// Custom SolyticsPopper is used (not nested MUI Menu) to avoid FocusTrap conflicts.
// ─────────────────────────────────────────────────────────────────────────────

const NestedMenuItem = React.forwardRef(
  ({ item, menuOpen, onMenuClose, elevation, ...props }, ref) => {
    const [subOpen, setSubOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null); // use state for anchorEl instead of ref.current during render
    const triggerRef = useRef(null); // anchor for the Popper
    const subMenuRef = useRef(null); // root Paper of the submenu panel
    const closeTimer = useRef(null); // delayed-close timer

    const subMenuId = `${item.id}-submenu`;

    const handleTriggerRef = useCallback(
      (node) => {
        triggerRef.current = node;
        setAnchorEl(node); // Need node stored in state to pass safely to Popper's anchorEl
        if (typeof ref === 'function') ref(node);
        // eslint-disable-next-line no-param-reassign
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const openSub = useCallback((fromKeyboard = false) => {
      clearTimeout(closeTimer.current);
      setSubOpen(true);
      if (fromKeyboard) {
        // Delay focus slightly so React has time to render the Popper and MenuList to the DOM
        setTimeout(() => {
          if (subMenuRef.current) {
            const firstItem = subMenuRef.current.querySelector(
              '[role="menuitem"]:not([aria-disabled="true"])'
            );
            firstItem?.focus();
          }
        }, 0);
      }
    }, []);

    const closeSub = useCallback(() => {
      setSubOpen(false);
    }, []);

    const scheduleClose = useCallback(() => {
      closeTimer.current = setTimeout(closeSub, CLOSE_DELAY_MS);
    }, [closeSub]);

    const cancelScheduledClose = useCallback(() => {
      clearTimeout(closeTimer.current);
    }, []);

    // Cleanup pending timer on unmount
    useEffect(
      () => () => {
        clearTimeout(closeTimer.current);
      },
      []
    );

    // Cascade close: when root menu closes, submenus close immediately — no stagger
    const [prevMenuOpen, setPrevMenuOpen] = useState(menuOpen);
    if (menuOpen !== prevMenuOpen) {
      setPrevMenuOpen(menuOpen);
      if (!menuOpen) setSubOpen(false);
    }

    return (
      <>
        {/* ── Trigger row ───────────────────────────────────────────────── */}
        <MenuItem
          ref={handleTriggerRef}
          id={item.id}
          disabled={item.isItemDisabled}
          aria-haspopup="menu"
          aria-expanded={subOpen}
          aria-controls={subOpen ? subMenuId : undefined}
          {...props}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
          // Hover — primary open mechanism for mouse
          onMouseEnter={(e) => {
            props.onMouseEnter?.(e);
            if (!item.isItemDisabled) openSub(false);
          }}
          onMouseLeave={(e) => {
            props.onMouseLeave?.(e);
            // Don't close if cursor is entering the submenu panel
            if (subMenuRef.current?.contains(e.relatedTarget)) return;
            scheduleClose();
          }}
          // Click — open mechanism for touch devices; also toggles on mouse
          onClick={(e) => {
            props.onClick?.(e);
            if (!item.isItemDisabled) openSub(false);
          }}
          // Keyboard — ArrowRight / Enter to open
          onKeyDown={(e) => {
            props.onKeyDown?.(e);
            if (item.isItemDisabled) return;
            if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              openSub(true);
            }
          }}
        >
          <SolyticsBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {renderIcon(item.icon)}
            {isValidElement(item.content) ? (
              item.content
            ) : (
              <SolyticsTypography
                variant="inherit"
                sx={{ color: 'text.primary' }}
              >
                {item.content}
              </SolyticsTypography>
            )}
          </SolyticsBox>
          <RightArrowIcon
            style={{
              color: 'text.primary',
              flexShrink: 0,
              marginLeft: 8,
              width: 16,
              height: 16,
            }}
          />
        </MenuItem>

        {/* ── Submenu Popper ────────────────────────────────────────────── */}
        <SolyticsPopper
          open={subOpen}
          anchorEl={anchorEl}
          placement="right-start"
          transition
          slotProps={{ transition: { timeout: { enter: 50, exit: 0 } } }}
          // Sit above MUI Modal (dialogs etc.) so menus always appear on top
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          modifiers={[
            {
              name: 'flip',
              // Try these placements in order when the preferred one clips the viewport
              options: {
                fallbackPlacements: ['left-start', 'right-end', 'left-end'],
              },
            },
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8, altAxis: true },
            },
          ]}
        >
          <MenuList
            ref={subMenuRef}
            id={subMenuId}
            dense
            role="menu"
            aria-labelledby={item.id}
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={(e) => {
              // If the user's mouse moves from this submenu panel back to the parent item
              // that opened it, do not schedule a close. The submenu should remain open.
              if (triggerRef.current?.contains(e.relatedTarget)) return;
              scheduleClose();
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') {
                if (e.nativeEvent.__solyticsArrowLeftHandled) return;
                // eslint-disable-next-line no-param-reassign
                e.nativeEvent.__solyticsArrowLeftHandled = true;
                e.stopPropagation();
                closeSub();
                triggerRef.current?.focus();
              }
              // ArrowUp/Down: stop propagation so the parent MenuList
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.stopPropagation();
              }
            }}
            sx={{
              bgcolor: 'background.paper',
              minWidth: 160,
              boxShadow: (theme) => theme.shadows[elevation || 3],
              borderRadius: 1, // Matches typical Paper border-radius
              outline: 0,
            }}
          >
            {renderMenuItems(item.subMenus, menuOpen, onMenuClose, elevation)}
          </MenuList>
        </SolyticsPopper>
      </>
    );
  }
);
NestedMenuItem.displayName = 'NestedMenuItem';

// ─────────────────────────────────────────────────────────────────────────────
// renderMenuItems (internal, shared recursive renderer)
// Called from SolyticsMenus root AND from inside every NestedMenuItem.
// ─────────────────────────────────────────────────────────────────────────────

function renderMenuItems(items, menuOpen, onMenuClose, elevation) {
  if (!items?.length) return null;

  return items.flatMap((item) => {
    if (item.isItemVisible === false) return [];

    const node = item.subMenus?.length ? (
      <NestedMenuItem
        key={item.id}
        item={item}
        menuOpen={menuOpen}
        onMenuClose={onMenuClose}
        elevation={elevation}
      />
    ) : (
      <LeafMenuItem key={item.id} item={item} onMenuClose={onMenuClose} />
    );

    // divider:true → append a Divider immediately after this item
    if (item.divider) {
      return [<SolyticsDivider key={`${item.id}--divider`} />, node];
    }
    return [node];
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// SolyticsMenus — exported default
// ─────────────────────────────────────────────────────────────────────────────

function SolyticsMenus({
  id,
  items,
  anchorEl,
  open,
  onClose,
  elevation = 3,
  anchorReference,
  anchorPosition,
  paperSx,
}) {
  return (
    <Menu
      id={id}
      autoFocus
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      elevation={elevation}
      {...(anchorReference && { anchorReference })}
      {...(anchorPosition && { anchorPosition })}
      slots={{ transition: Fade }}
      slotProps={{
        paper: {
          sx: { minWidth: 180, maxWidth: 320, maxHeight: '70vh', ...paperSx },
        },
      }}
      // Auto-focus first non-disabled item on open — helps keyboard users
      autoFocusItem
    >
      {renderMenuItems(items, open, onClose, elevation)}
    </Menu>
  );
}

SolyticsMenus.propTypes = {
  /** Unique menu id — also set as aria-controls on the trigger button */
  id: PropTypes.string.isRequired,

  /** Array of menu items — see Item Shape in module JSDoc above */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
        .isRequired,
      onClick: PropTypes.func,
      icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
      isItemDisabled: PropTypes.bool,
      isItemVisible: PropTypes.bool,
      divider: PropTypes.bool,
    })
  ).isRequired,

  /** Anchor element — typically `event.currentTarget` from the trigger's onClick */

  /** Controlled open state */
  open: PropTypes.bool.isRequired,

  /** Called when the menu should close (outside click, Escape key) */
  onClose: PropTypes.func.isRequired,

  /** Paper elevation — applied to the root menu and all nested submenu panels */
  elevation: PropTypes.number,

  /** Use 'anchorPosition' to position via { top, left } coordinates instead of anchorEl */
  anchorReference: PropTypes.oneOf(['anchorEl', 'anchorPosition', 'none']),

  /** Screen coordinates { top: number, left: number } — required when anchorReference='anchorPosition' */
  anchorPosition: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
  }),

  /** Additional styles for the root menu paper */
  paperSx: PropTypes.shape({}),
};

export default SolyticsMenus;
